import { Injectable } from '@angular/core';
import { QUIZ_HISTORY_TYPE, QuizAnswerLogEntry } from '@shared/ProjectKeyValStore';
import { BotService } from './bot.service';
import { ProjectKeyValService } from './project-key-val.service';

/** Protokolliert und liest die Quiz-Antwort-Historie eines Nutzers (über `RAGAI.PROJECTKEYVAL`). */
@Injectable({
    providedIn: 'root'
})
export class QuizHistoryService {

    constructor(
        private projectKeyVal: ProjectKeyValService,
        private botService: BotService
    ) { }

    /**
     * Protokolliert eine beantwortete Quiz-Frage projektweit für den aktuellen Nutzer
     * (RAGAI.PROJECTKEYVAL / QUIZ_HISTORY_TYPE), analog zu RAGAI.GROUPCHAT.
     * @param entry Der zu protokollierende Quiz-Antwort-Eintrag (ohne Bot-Metadaten, werden automatisch ergänzt).
     */
    public async logAnswer(entry: Omit<QuizAnswerLogEntry, 'botId' | 'botName'>): Promise<void> {
        const fullEntry: QuizAnswerLogEntry = {
            ...entry,
            botId: this.botService.botConfig?._id ?? '',
            botName: this.botService.botConfig?.name ?? 'Unbekannter Bot'
        };
        await this.projectKeyVal.unique('answer', JSON.stringify(fullEntry), QUIZ_HISTORY_TYPE);
    }

    /**
     * Liefert die komplette Quiz-Historie des aktuellen Nutzers (projektweit, botübergreifend),
     * chronologisch aufsteigend sortiert (älteste zuerst).
     * @returns Die Quiz-Antwort-Historie.
     */
    public async getHistory(): Promise<QuizAnswerLogEntry[]> {
        const entries = await this.projectKeyVal.getByType(QUIZ_HISTORY_TYPE);
        return entries
            .map((e) => {
                try {
                    return JSON.parse(e.value as string) as QuizAnswerLogEntry;
                } catch {
                    return null;
                }
            })
            .filter((e): e is QuizAnswerLogEntry => e != null)
            .sort((a, b) => a.askedAt.localeCompare(b.askedAt));
    }

    /**
     * Formatiert die letzten `limit` Einträge der Historie als kompakten Text-Block, der in den
     * Quiz-Generierungs-Prompt eingebettet werden kann. `limit <= 0` bedeutet: keine Begrenzung.
     * @param limit Maximale Anzahl an Einträgen (0 oder negativ = alle).
     * @returns Der formatierte Text-Block.
     */
    public async getHistoryAsPromptText(limit: number): Promise<string> {
        const history = await this.getHistory();
        const relevant = limit > 0 ? history.slice(-limit) : history;
        if (relevant.length === 0) return '';
        return relevant
            .map((e) => `- Frage: "${e.question}" | Antwort des Nutzers: "${e.givenAnswer}" | Richtig beantwortet: ${e.correct ? 'ja' : 'nein'} | Zeitpunkt: ${e.answeredAt}`)
            .join('\n');
    }

    /**
     * Löscht den kompletten Quiz-Verlauf des aktuellen Nutzers für dieses Projekt.
     */
    public async clearHistory(): Promise<void> {
        const entries = await this.projectKeyVal.getByType(QUIZ_HISTORY_TYPE);
        for (const entry of entries) {
            await this.projectKeyVal.deleteKeyValEntry(entry.key, entry.type);
        }
    }
}
